import { useIsAuthenticated, useLogin } from "@refinedev/core";
import { useState } from "react";
import { Navigate } from "react-router";

type LoginVars = { password: string };

/**
 * Дверь. Одно поле, и ни слова о том, как завести пароль.
 *
 * Заводят и отзывают пароль в CRM, поэтому здесь нет ни регистрации, ни
 * восстановления: ссылка на них была бы обещанием, которого этот экран не
 * исполняет. Логина нет тоже — пароль сам называет, кто пришёл.
 */
export function LoginPage() {
  const { data: auth } = useIsAuthenticated();
  const { mutate: login, isPending } = useLogin<LoginVars>();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  if (auth?.authenticated) return <Navigate to="/placement" replace />;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    login(
      { password },
      {
        onSuccess: (result) => {
          if (!result.success)
            setError(result.error?.message ?? "Не удалось войти");
        },
      },
    );
  };

  return (
    <div
      className="grid h-screen place-items-center bg-bg px-4"
      data-component="LoginPage"
      data-annot="вход"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-[340px] rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6"
      >
        <div className="mb-5 flex items-center gap-2.5">
          <div className="grid size-6 place-items-center rounded-[5px] bg-fg text-[13px] font-semibold text-bg">
            W
          </div>
          <div className="text-[15px] font-semibold tracking-[-0.015em]">
            wms
          </div>
        </div>

        <h1 className="mb-1 text-[17px] font-semibold tracking-[-0.02em]">
          Вход на склад
        </h1>
        <p className="mb-5 text-[12.5px] text-fg-muted">
          Пароль сотрудника — тот же, что в CRM.
        </p>

        {/* Подпись видимая, а не placeholder: placeholder исчезает при первом
            же символе, и человек, отвлёкшийся на середине ввода, теряет
            единственное указание, что от него хотели. */}
        <label
          className="mb-1.5 block text-[12px] text-fg-muted"
          htmlFor="password"
        >
          Пароль
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          autoFocus
          disabled={isPending}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "login-error" : undefined}
          className="mb-4 h-9 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 text-[13.5px] focus:border-border-strong"
        />

        {error ? (
          /* role="alert" — чтобы отказ дошёл и до того, кто экран не видит. */
          <div
            id="login-error"
            role="alert"
            className="mb-3.5 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 py-2 text-[12.5px] text-fg"
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={isPending || password.length < 4}
          className="h-9 w-full rounded-[var(--radius-ui)] bg-fg text-[13.5px] font-medium text-bg disabled:opacity-60"
        >
          {isPending ? "Проверяю…" : "Войти"}
        </button>
      </form>
    </div>
  );
}
